import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Slider,
  Switch,
  TextField,
  InputAdornment,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  SwatchIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import { financeApi, PaymentMode } from '../../api/financeApi';
import { CURRENCY } from '../../config/currency';
import { formatCurrency } from '../../config/currency';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import TransactionRulesManager from '../../components/finance/TransactionRulesManager';
import { PrimaryButton, SecondaryButton, AppIconButton, PageHeroBand } from '../../components/ui';

// --- Types ---

interface FinanceSettingsData {
  foodCostTarget: number;
  defaultPaymentMode: string;
  staffMealAllowance: number;
  posIncomeSync: boolean;
  ocrConfidenceThreshold: number;
}

interface CategoryItem {
  id: number;
  name: string;
  type: string;
  isSystem?: boolean;
  monthlyBudget?: number;
  currentMonthSpent?: number;
}

interface NewCategoryForm {
  name: string;
}

interface RevenueChannel {
  id: number;
  name: string;
  type: string;
  commissionPercent: number;
  settlementDays: number;
  color: string;
  isActive: boolean;
  isDefault?: boolean;
  recordRevenueAsIncome?: boolean;
}

interface NewChannelForm {
  name: string;
  type: string;
  commissionPercent: number | '';
  settlementDays: number | '';
  color: string;
  // Tri-state:
  //  - null     → user has not toggled; backend computes default from type
  //               (ONLINE_AGGREGATOR=false, else=true) on create.
  //  - true     → income recorded immediately at EOD.
  //  - false    → income only recorded when an aggregator settlement is saved.
  recordRevenueAsIncome: boolean | null;
}

const CHANNEL_TYPES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
  { value: 'UPI', label: 'UPI' },
  { value: 'ONLINE_AGGREGATOR', label: 'Online Aggregator' },
  { value: 'CUSTOM', label: 'Custom' },
];

// Only Cash and Card are non-deletable defaults. UPI — even if `isDefault` is
// true on legacy rows — is deletable. Backend migration V160 flips legacy UPI
// rows to is_default=false; this guard is defense-in-depth on the UI side.
const isProtectedDefault = (ch: RevenueChannel): boolean =>
  Boolean(ch.isDefault) && (ch.type === 'CASH' || ch.type === 'CARD');

const COLOR_PRESETS = [
  '#4CAF50', '#2196F3', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#795548', '#607D8B', '#F44336',
];

const DEFAULT_SETTINGS: FinanceSettingsData = {
  foodCostTarget: 32,
  defaultPaymentMode: 'CASH',
  staffMealAllowance: 150,
  posIncomeSync: false,
  ocrConfidenceThreshold: 70,
};

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
  { value: 'CREDIT', label: 'Credit' },
];

// --- Component ---

const FinanceSettings: React.FC = () => {
  const { theme } = useTheme();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  // Modal overlay shown when the page is reached from the Aggregators banner
  // (?modal=aggregator). Lets the vendor flip an existing channel's type to
  // ONLINE_AGGREGATOR without hunting for the edit drawer.
  const aggregatorModalOpen = searchParams.get('modal') === 'aggregator';
  const [togglingChannelId, setTogglingChannelId] = useState<number | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<FinanceSettingsData>(DEFAULT_SETTINGS);
  const [categories, setCategories] = useState<CategoryItem[]>([]);

  // Revenue channels
  const [channels, setChannels] = useState<RevenueChannel[]>([]);
  const [addingChannel, setAddingChannel] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<number | null>(null);
  const [newChannel, setNewChannel] = useState<NewChannelForm>({
    name: '',
    type: 'CUSTOM',
    commissionPercent: '',
    settlementDays: '',
    color: COLOR_PRESETS[0],
    recordRevenueAsIncome: null,
  });
  const [savingChannel, setSavingChannel] = useState(false);

  // Visual hint only — actual default still computed server-side. Aggregators
  // default OFF; everything else defaults ON.
  const suggestedRecordAsIncome = (channelType: string): boolean =>
    channelType !== 'ONLINE_AGGREGATOR';

  // Effective switch state for the form: user's explicit choice wins; otherwise
  // fall back to the by-type suggestion so the toggle isn't visually confusing.
  const effectiveRecordAsIncome = (form: NewChannelForm): boolean =>
    form.recordRevenueAsIncome === null || form.recordRevenueAsIncome === undefined
      ? suggestedRecordAsIncome(form.type)
      : form.recordRevenueAsIncome;

  // Budget auto-save debounce
  const [budgetTimers, setBudgetTimers] = useState<Record<number, ReturnType<typeof setTimeout>>>({});

  // Action menu state (shared for channels + categories)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuActiveId, setMenuActiveId] = useState<number | null>(null);
  const [menuSection, setMenuSection] = useState<'channel' | 'category' | null>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, id: number, section: 'channel' | 'category') => {
    setMenuAnchorEl(e.currentTarget);
    setMenuActiveId(id);
    setMenuSection(section);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuActiveId(null);
    setMenuSection(null);
  };

  // Transaction rules - now managed by TransactionRulesManager component

  // Category management
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState<NewCategoryForm>({ name: '' });
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  // Payment mode management
  const [paymentModes, setPaymentModes] = useState<PaymentMode[]>([]);
  const [addingPaymentMode, setAddingPaymentMode] = useState(false);
  const [newPaymentModeName, setNewPaymentModeName] = useState('');
  const [savingPaymentMode, setSavingPaymentMode] = useState(false);
  const [editingPaymentModeId, setEditingPaymentModeId] = useState<number | null>(null);
  const [editingPaymentModeDisplayName, setEditingPaymentModeDisplayName] = useState('');

  // --- Fetch ---

  const fetchSettings = useCallback(async () => {
    try {
      const res = await financeApi.getSettings();
      const d = res.data;
      setSettings({
        foodCostTarget: d.foodCostTarget ?? DEFAULT_SETTINGS.foodCostTarget,
        defaultPaymentMode: d.defaultPaymentMode ?? DEFAULT_SETTINGS.defaultPaymentMode,
        staffMealAllowance: d.staffMealAllowance ?? DEFAULT_SETTINGS.staffMealAllowance,
        posIncomeSync: d.posIncomeSync ?? DEFAULT_SETTINGS.posIncomeSync,
        ocrConfidenceThreshold: (d.ocrConfidenceThreshold != null && d.ocrConfidenceThreshold <= 1)
          ? Math.round(d.ocrConfidenceThreshold * 100)
          : (d.ocrConfidenceThreshold ?? DEFAULT_SETTINGS.ocrConfidenceThreshold),
      });
    } catch (err: unknown) {
      console.error('Error fetching finance settings:', err);
      // Use defaults on error
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await financeApi.getCategories();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setCategories(data);
    } catch (err: unknown) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const res = await financeApi.getChannels();
      const data = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setChannels(data);
    } catch (err: unknown) {
      console.error('Error fetching channels:', err);
    }
  }, []);

  const fetchPaymentModes = useCallback(async () => {
    try {
      const res = await financeApi.getPaymentModes();
      const data = Array.isArray(res.data) ? res.data : [];
      data.sort((a, b) => a.sortOrder - b.sortOrder || a.displayName.localeCompare(b.displayName));
      setPaymentModes(data);
    } catch (err: unknown) {
      console.error('Error fetching payment modes:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchCategories(), fetchChannels(), fetchPaymentModes()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Payment Mode handlers ---
  const handleAddPaymentMode = async () => {
    const trimmed = newPaymentModeName.trim();
    if (!trimmed) {
      toast.error('Payment mode name is required');
      return;
    }
    const canonical = trimmed.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!canonical) {
      toast.error('Invalid payment mode name');
      return;
    }
    setSavingPaymentMode(true);
    try {
      await financeApi.createPaymentMode({ name: canonical, displayName: trimmed });
      toast.success('Payment mode added');
      setNewPaymentModeName('');
      setAddingPaymentMode(false);
      await fetchPaymentModes();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error('A payment mode with this name already exists');
      } else {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to add payment mode';
        toast.error(msg);
      }
    } finally {
      setSavingPaymentMode(false);
    }
  };

  const handleTogglePaymentMode = async (mode: PaymentMode) => {
    try {
      await financeApi.updatePaymentMode(mode.id, { isActive: !mode.isActive });
      await fetchPaymentModes();
    } catch (err: unknown) {
      console.error('Error toggling payment mode:', err);
      toast.error('Failed to update payment mode');
    }
  };

  const handleSavePaymentModeDisplayName = async (id: number) => {
    const trimmed = editingPaymentModeDisplayName.trim();
    if (!trimmed) {
      toast.error('Display name is required');
      return;
    }
    try {
      await financeApi.updatePaymentMode(id, { displayName: trimmed });
      toast.success('Payment mode updated');
      setEditingPaymentModeId(null);
      setEditingPaymentModeDisplayName('');
      await fetchPaymentModes();
    } catch (err: unknown) {
      console.error('Error updating payment mode:', err);
      toast.error('Failed to update payment mode');
    }
  };

  const handleDeletePaymentMode = async (mode: PaymentMode) => {
    const confirmed = await confirm({
      title: 'Delete payment mode',
      description: `Delete payment mode "${mode.displayName}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;
    try {
      await financeApi.deletePaymentMode(mode.id);
      toast.success('Payment mode deleted');
      await fetchPaymentModes();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        toast.error('Payment mode is in use — cannot delete');
      } else {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to delete payment mode';
        toast.error(msg);
      }
    }
  };

  // --- Handlers ---

  const handleSave = async () => {
    setSaving(true);
    try {
      await financeApi.updateSettings(settings as unknown as Record<string, unknown>);
      toast.success('Settings saved');
    } catch (err: unknown) {
      console.error('Error saving settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    setSavingCategory(true);
    try {
      await financeApi.createCategory({ name: newCategory.name.trim(), type: 'EXPENSE' });
      toast.success('Category added');
      setNewCategory({ name: '' });
      setAddingCategory(false);
      await fetchCategories();
    } catch (err: unknown) {
      console.error('Error adding category:', err);
      toast.error('Failed to add category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    try {
      await financeApi.createCategory({ id, _action: 'delete' });
      toast.success('Category removed');
      await fetchCategories();
    } catch (err: unknown) {
      console.error('Error deleting category:', err);
      toast.error('Failed to delete category');
    }
  };

  const handleUpdateCategory = async (id: number) => {
    if (!editingCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    setSavingCategory(true);
    try {
      await financeApi.createCategory({ id, name: editingCategoryName.trim(), _action: 'update' });
      toast.success('Category updated');
      setEditingCategoryId(null);
      setEditingCategoryName('');
      await fetchCategories();
    } catch (err: unknown) {
      console.error('Error updating category:', err);
      toast.error('Failed to update category');
    } finally {
      setSavingCategory(false);
    }
  };

  // --- Channel handlers ---

  const resetChannelForm = () => {
    setNewChannel({
      name: '',
      type: 'CUSTOM',
      commissionPercent: '',
      settlementDays: '',
      color: COLOR_PRESETS[0],
      recordRevenueAsIncome: null,
    });
    setEditingChannelId(null);
  };

  const handleOpenEditChannel = (ch: RevenueChannel) => {
    setEditingChannelId(ch.id);
    setNewChannel({
      name: ch.name,
      type: ch.type,
      commissionPercent: ch.commissionPercent ?? 0,
      settlementDays: ch.settlementDays ?? 0,
      color: ch.color || COLOR_PRESETS[0],
      // For edit: hydrate the explicit flag value from the server so the
      // switch reflects current state and any save sends an explicit boolean.
      recordRevenueAsIncome: ch.recordRevenueAsIncome ?? suggestedRecordAsIncome(ch.type),
    });
    setAddingChannel(true);
  };

  const handleAddChannel = async () => {
    if (!newChannel.name.trim()) {
      toast.error('Channel name is required');
      return;
    }
    setSavingChannel(true);
    try {
      const payload: Record<string, unknown> = {
        name: newChannel.name.trim(),
        type: newChannel.type,
        commissionPercent: Number(newChannel.commissionPercent) || 0,
        settlementDays: Number(newChannel.settlementDays) || 0,
        color: newChannel.color,
      };
      // Only send the flag when user has explicitly toggled (or we're editing
      // an existing channel). Omitting on create lets the backend compute
      // the type-based default (ONLINE_AGGREGATOR=false, else=true).
      if (newChannel.recordRevenueAsIncome !== null && newChannel.recordRevenueAsIncome !== undefined) {
        payload.recordRevenueAsIncome = newChannel.recordRevenueAsIncome;
      }

      if (editingChannelId != null) {
        await financeApi.updateChannel(editingChannelId, payload);
        toast.success('Channel updated');
      } else {
        await financeApi.createChannel(payload);
        toast.success('Channel added');
      }
      resetChannelForm();
      setAddingChannel(false);
      await fetchChannels();
    } catch (err: unknown) {
      console.error('Error saving channel:', err);
      toast.error(editingChannelId != null ? 'Failed to update channel' : 'Failed to add channel');
    } finally {
      setSavingChannel(false);
    }
  };

  const handleToggleChannel = async (ch: RevenueChannel) => {
    try {
      await financeApi.updateChannel(ch.id, { ...ch, isActive: !ch.isActive });
      setChannels((prev) => prev.map((c) => c.id === ch.id ? { ...c, isActive: !c.isActive } : c));
      toast.success(`${ch.name} ${ch.isActive ? 'disabled' : 'enabled'}`);
    } catch (err: unknown) {
      console.error('Error toggling channel:', err);
      toast.error('Failed to update channel');
    }
  };

  /**
   * Flip a channel's type to ONLINE_AGGREGATOR from the onboarding modal.
   * Backend service performs a partial update — only `type` is sent so
   * commissionPercent/settlementDays stay untouched.
   */
  const handleMarkAsAggregator = async (ch: RevenueChannel) => {
    setTogglingChannelId(ch.id);
    try {
      await financeApi.updateChannel(ch.id, { type: 'ONLINE_AGGREGATOR' });
      setChannels((prev) =>
        prev.map((c) => (c.id === ch.id ? { ...c, type: 'ONLINE_AGGREGATOR' } : c)),
      );
      toast.success(`${ch.name} marked as aggregator`);
    } catch (err: unknown) {
      console.error('Error marking channel as aggregator:', err);
      toast.error('Failed to update channel');
    } finally {
      setTogglingChannelId(null);
    }
  };

  const closeAggregatorModal = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('modal');
    setSearchParams(next);
  };

  const handleDeleteChannel = async (id: number) => {
    try {
      await financeApi.deleteChannel(id);
      toast.success('Channel removed');
      await fetchChannels();
    } catch (err: unknown) {
      console.error('Error deleting channel:', err);
      toast.error('Failed to delete channel');
    }
  };

  // --- Budget handlers ---

  const handleBudgetChange = (categoryId: number, value: number) => {
    // Update local state immediately
    setCategories((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, monthlyBudget: value } : c))
    );

    // Debounced auto-save
    if (budgetTimers[categoryId]) {
      clearTimeout(budgetTimers[categoryId]);
    }
    const timer = setTimeout(async () => {
      try {
        await financeApi.updateCategoryBudget(categoryId, value);
        toast.success('Budget saved', { id: `budget-${categoryId}` });
      } catch (err: unknown) {
        console.error('Error saving budget:', err);
        toast.error('Failed to save budget');
      }
    }, 800);
    setBudgetTimers((prev) => ({ ...prev, [categoryId]: timer }));
  };

  // --- Shared styles ---

  const cardSx = {
    p: { xs: 2, sm: 3 },
    borderRadius: '12px',
    backgroundColor: theme.components?.card?.background || '#FFFFFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
    mb: { xs: 2, sm: 3 },
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  const sectionTitle = (text: string, description?: string) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        sx={{
          fontWeight: 700,
          color: theme.text,
          fontSize: { xs: '1rem', sm: '1.1rem' },
          lineHeight: 1.3,
        }}
      >
        {text}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          sx={{ color: theme.gray?.[500] || '#666', mt: 0.25, fontSize: '0.813rem' }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );

  // --- Loading ---

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh' }}>
        <Skeleton variant="text" width={200} height={40} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', mb: 2 }} />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            height={120}
            sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0', mb: 2, borderRadius: '12px' }}
          />
        ))}
      </Box>
    );
  }

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 4 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand eyebrow="Finance · Settings" title="Finance Settings" />

      {/* General Settings */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle('General Settings')}

        {/* Food Cost Target */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: theme.text, fontWeight: 600, fontSize: '0.875rem' }}>
              Food Cost Target
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: theme.primary,
                fontSize: '1rem',
                minWidth: 48,
                textAlign: 'right',
              }}
            >
              {settings.foodCostTarget}%
            </Typography>
          </Box>
          <Slider
            value={settings.foodCostTarget}
            onChange={(_, val) => setSettings((prev) => ({ ...prev, foodCostTarget: val as number }))}
            min={0}
            max={100}
            step={1}
            sx={{
              color: theme.primary,
              '& .MuiSlider-thumb': {
                backgroundColor: theme.primary,
                width: 20,
                height: 20,
                '&:hover, &.Mui-focusVisible': { boxShadow: `0 0 0 6px ${theme.primary}22` },
              },
              '& .MuiSlider-track': { backgroundColor: theme.primary },
              '& .MuiSlider-rail': { backgroundColor: theme.gray?.[200] || '#E0E0E0' },
            }}
          />
          <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.7rem' }}>
            The food cost percentage you aim to maintain. Dashboard gauge shows performance against this target.
          </Typography>
        </Box>

        {/* Default Payment Mode */}
        <Box>
          <FormControl fullWidth size="small" sx={inputSx}>
            <InputLabel>Default Payment Mode</InputLabel>
            <Select
              value={settings.defaultPaymentMode}
              onChange={(e) => setSettings((prev) => ({ ...prev, defaultPaymentMode: e.target.value }))}
              label="Default Payment Mode"
            >
              {PAYMENT_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Staff Meals */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle('Staff Meals', 'Configure staff meal allowance for non-revenue cost tracking')}

        <TextField
          label="Daily Staff Meal Allowance per Person"
          type="number"
          size="small"
          value={settings.staffMealAllowance}
          onChange={(e) => setSettings((prev) => ({ ...prev, staffMealAllowance: Number(e.target.value) || 0 }))}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '0.95rem' }}>
                  {CURRENCY.symbol}
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={inputSx}
          inputProps={{ min: 0, step: 10 }}
        />
      </Paper>

      {/* POS Integration */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle('POS Integration')}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="body2" sx={{ color: theme.text, fontWeight: 600, fontSize: '0.875rem' }}>
              Enable POS Income Sync
            </Typography>
            <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.75rem' }}>
              Automatically pull income from completed POS orders
            </Typography>
          </Box>
          <Switch
            checked={settings.posIncomeSync}
            onChange={(e) => setSettings((prev) => ({ ...prev, posIncomeSync: e.target.checked }))}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary },
              '& .MuiSwitch-track': { backgroundColor: theme.gray?.[300] || '#CCC' },
            }}
          />
        </Box>
      </Paper>

      {/* Revenue Channels */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle('Revenue Channels', 'Configure sales channels for revenue tracking')}

        {/* Channel list */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {channels.map((ch) => (
            <Box
              key={ch.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                backgroundColor: ch.isActive ? `${ch.color}08` : `${theme.gray?.[100] || '#F0F0F0'}`,
                border: `1px solid ${ch.isActive ? `${ch.color}33` : (theme.gray?.[200] || '#E0E0E0')}`,
                opacity: ch.isActive ? 1 : 0.6,
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              {/* Color dot + name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: ch.color || theme.primary,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: theme.text,
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {ch.name}
                  </Typography>
                  {ch.recordRevenueAsIncome === false && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.gray?.[500] || '#666',
                        fontSize: '0.65rem',
                        display: 'block',
                        lineHeight: 1.2,
                      }}
                    >
                      Records on settlement
                    </Typography>
                  )}
                </Box>
                {isProtectedDefault(ch) && (
                  <Chip
                    label="Default"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      backgroundColor: `${theme.gray?.[200] || '#E0E0E0'}`,
                      color: theme.gray?.[500] || '#666',
                    }}
                  />
                )}
              </Box>

              {/* Type badge */}
              <Chip
                label={CHANNEL_TYPES.find((t) => t.value === ch.type)?.label || ch.type}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  backgroundColor: `${ch.color}18`,
                  color: ch.color,
                  border: `1px solid ${ch.color}33`,
                }}
              />

              {/* Active toggle */}
              <Switch
                checked={ch.isActive}
                onChange={() => handleToggleChannel(ch)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: ch.color || theme.primary },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ch.color || theme.primary },
                  '& .MuiSwitch-track': { backgroundColor: theme.gray?.[300] || '#CCC' },
                }}
              />

              {/* Actions menu — visible for every channel so the user can edit
                  the per-channel income flag. Delete is hidden for protected
                  defaults (Cash/Card) inside the menu itself. */}
              <AppIconButton
                icon={<EllipsisVerticalIcon style={{ width: 16, height: 16 }} />}
                tooltip="Actions"
                variant="ghost"
                size="small"
                onClick={(e) => handleMenuOpen(e, ch.id, 'channel')}
              />
            </Box>
          ))}
        </Box>

        {/* Add Channel form */}
        {addingChannel ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              borderRadius: '10px',
              border: `1px solid ${theme.primary}33`,
              backgroundColor: `${theme.primary}06`,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <TextField
                size="small"
                placeholder="Channel name"
                value={newChannel.name}
                onChange={(e) => setNewChannel((prev) => ({ ...prev, name: e.target.value }))}
                autoFocus
                sx={{ ...inputSx, flex: 1, minWidth: 140 }}
                inputProps={{ style: { fontSize: '0.875rem', padding: '8px 12px' } }}
              />
              <FormControl size="small" sx={{ ...inputSx, minWidth: 140 }}>
                <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary }, fontSize: '0.875rem' }}>
                  Type
                </InputLabel>
                <Select
                  value={newChannel.type}
                  label="Type"
                  onChange={(e) => setNewChannel((prev) => ({ ...prev, type: e.target.value }))}
                  sx={{ fontSize: '0.875rem' }}
                >
                  {CHANNEL_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: { xs: 'wrap', sm: 'nowrap' }, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Commission %"
                type="number"
                value={newChannel.commissionPercent}
                onChange={(e) => setNewChannel((prev) => ({ ...prev, commissionPercent: e.target.value ? Number(e.target.value) : '' }))}
                sx={{ ...inputSx, maxWidth: 130 }}
                inputProps={{ min: 0, max: 100, step: 0.5, style: { fontSize: '0.875rem', padding: '8px 12px' } }}
              />
              <TextField
                size="small"
                label="Settlement days"
                type="number"
                value={newChannel.settlementDays}
                onChange={(e) => setNewChannel((prev) => ({ ...prev, settlementDays: e.target.value ? Number(e.target.value) : '' }))}
                sx={{ ...inputSx, maxWidth: 130 }}
                inputProps={{ min: 0, max: 60, step: 1, style: { fontSize: '0.875rem', padding: '8px 12px' } }}
              />

              {/* Color picker presets */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <SwatchIcon style={{ width: 16, height: 16, color: theme.gray?.[400] || '#999' }} />
                {COLOR_PRESETS.map((c) => (
                  <Box
                    key={c}
                    onClick={() => setNewChannel((prev) => ({ ...prev, color: c }))}
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: c,
                      cursor: 'pointer',
                      border: newChannel.color === c ? '2px solid #333' : '2px solid transparent',
                      '&:hover': { transform: 'scale(1.15)' },
                      transition: 'all 0.15s ease',
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Record revenue as income — per-channel switch.
                Mobile-first: stacks vertically on xs (label above, switch below). */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: { xs: 0.5, sm: 2 },
                px: 1.25,
                py: 1.25,
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#FFFFFF',
                border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.text, fontWeight: 600, fontSize: '0.875rem' }}
                >
                  Record revenue as income
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.gray?.[500] || '#666',
                    fontSize: '0.75rem',
                    display: 'block',
                    mt: 0.25,
                  }}
                >
                  Off = income recorded only when settlement is saved (matches actual bank payout). Recommended off for aggregators.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                <Switch
                  checked={effectiveRecordAsIncome(newChannel)}
                  onChange={(e) => setNewChannel((prev) => ({ ...prev, recordRevenueAsIncome: e.target.checked }))}
                  inputProps={{ 'aria-label': 'Record revenue as income' }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: theme.primary },
                    '& .MuiSwitch-track': { backgroundColor: theme.gray?.[300] || '#CCC' },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <SecondaryButton
                size="small"
                onClick={() => { setAddingChannel(false); resetChannelForm(); }}
                sx={{ fontSize: '0.813rem' }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                size="small"
                onClick={handleAddChannel}
                disabled={!newChannel.name.trim()}
                loading={savingChannel}
                startIcon={!savingChannel ? <CheckIcon style={{ width: 16, height: 16 }} /> : undefined}
                sx={{ fontSize: '0.813rem', minHeight: 36 }}
              >
                {editingChannelId != null ? 'Update Channel' : 'Save Channel'}
              </PrimaryButton>
            </Box>
          </Box>
        ) : (
          <SecondaryButton
            size="small"
            startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
            onClick={() => setAddingChannel(true)}
            sx={{ fontSize: '0.813rem' }}
          >
            Add Channel
          </SecondaryButton>
        )}
      </Paper>

      {/* OCR Settings */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle('OCR Settings', 'Configure invoice scanning accuracy thresholds')}

        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: theme.text, fontWeight: 600, fontSize: '0.875rem' }}>
              OCR Confidence Threshold
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: settings.ocrConfidenceThreshold >= 70 ? (theme.success || '#4CAF50') : (theme.warning || '#FFC107'),
                fontSize: '1rem',
                minWidth: 48,
                textAlign: 'right',
              }}
            >
              {settings.ocrConfidenceThreshold}%
            </Typography>
          </Box>
          <Slider
            value={settings.ocrConfidenceThreshold}
            onChange={(_, val) => setSettings((prev) => ({ ...prev, ocrConfidenceThreshold: val as number }))}
            min={0}
            max={100}
            step={5}
            sx={{
              color: settings.ocrConfidenceThreshold >= 70 ? (theme.success || '#4CAF50') : (theme.warning || '#FFC107'),
              '& .MuiSlider-thumb': {
                width: 20,
                height: 20,
              },
              '& .MuiSlider-rail': { backgroundColor: theme.gray?.[200] || '#E0E0E0' },
            }}
          />
          <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.7rem' }}>
            Below this threshold, the system will use AI for better extraction instead of raw OCR results.
          </Typography>
        </Box>
      </Paper>

      {/* Custom Expense Categories with Budget Caps */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle('Expense Categories & Budget Caps', 'Manage categories and set monthly spending limits')}

        {/* System categories (read-only) */}
        {categories.filter((c) => c.isSystem).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.gray?.[400] || '#999',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.05em',
                mb: 1,
                display: 'block',
              }}
            >
              System Categories
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {categories.filter((c) => c.isSystem).map((cat) => (
                <Box
                  key={cat.id}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '8px',
                    backgroundColor: theme.gray?.[100] || '#F0F0F0',
                    border: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                  }}
                >
                  <Typography variant="body2" sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', fontWeight: 500 }}>
                    {cat.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Custom categories (editable) */}
        <Box sx={{ mb: 2 }}>
          {categories.filter((c) => !c.isSystem).length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: theme.gray?.[400] || '#999',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: '0.05em',
                mb: 1,
                display: 'block',
              }}
            >
              Custom Categories
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {categories.filter((c) => !c.isSystem).map((cat) => (
              <Box
                key={cat.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: '10px',
                  backgroundColor: `${theme.primary}06`,
                  border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                }}
              >
                {editingCategoryId === cat.id ? (
                  <>
                    <TextField
                      size="small"
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      autoFocus
                      sx={{ ...inputSx, flex: 1 }}
                      inputProps={{ style: { fontSize: '0.875rem', padding: '6px 12px' } }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateCategory(cat.id);
                        if (e.key === 'Escape') { setEditingCategoryId(null); setEditingCategoryName(''); }
                      }}
                    />
                    <AppIconButton
                      icon={<CheckIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Save"
                      variant="ghost"
                      size="small"
                      onClick={() => handleUpdateCategory(cat.id)}
                      disabled={savingCategory}
                      sx={{ color: theme.success, '&:hover': { backgroundColor: `${theme.success}18`, color: theme.success } }}
                    />
                    <AppIconButton
                      icon={<XMarkIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Cancel"
                      variant="ghost"
                      size="small"
                      onClick={() => { setEditingCategoryId(null); setEditingCategoryName(''); }}
                    />
                  </>
                ) : (
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: theme.text, fontSize: '0.875rem', fontWeight: 500 }}
                        >
                          {cat.name}
                        </Typography>
                      </Box>
                      {/* Budget progress bar */}
                      {cat.monthlyBudget != null && cat.monthlyBudget > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
                            <Typography variant="caption" sx={{ color: theme.gray?.[400] || '#999', fontSize: '0.6rem' }}>
                              {formatCurrency(cat.currentMonthSpent || 0, { showDecimals: false })} / {formatCurrency(cat.monthlyBudget, { showDecimals: false })}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.6rem',
                                color: ((cat.currentMonthSpent || 0) / cat.monthlyBudget) >= 1
                                  ? (theme.danger || '#D99982')
                                  : ((cat.currentMonthSpent || 0) / cat.monthlyBudget) >= 0.8
                                    ? (theme.warning || '#FF9800')
                                    : (theme.success || '#4CAF50'),
                              }}
                            >
                              {Math.round(((cat.currentMonthSpent || 0) / cat.monthlyBudget) * 100)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(((cat.currentMonthSpent || 0) / cat.monthlyBudget) * 100, 100)}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: theme.gray?.[200] || '#E8E5E0',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                                backgroundColor: ((cat.currentMonthSpent || 0) / cat.monthlyBudget) >= 1
                                  ? (theme.danger || '#D99982')
                                  : ((cat.currentMonthSpent || 0) / cat.monthlyBudget) >= 0.8
                                    ? (theme.warning || '#FF9800')
                                    : (theme.success || '#4CAF50'),
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Budget input */}
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Budget"
                      value={cat.monthlyBudget || ''}
                      onChange={(e) => handleBudgetChange(cat.id, e.target.value ? Number(e.target.value) : 0)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.75rem' }}>
                              {CURRENCY.symbol}
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        ...inputSx,
                        width: { xs: 100, sm: 120 },
                        '& .MuiOutlinedInput-root': {
                          ...inputSx['& .MuiOutlinedInput-root'],
                          minHeight: 32,
                        },
                      }}
                      inputProps={{ min: 0, step: 100, style: { fontSize: '0.8rem', padding: '4px 8px' } }}
                    />

                    <AppIconButton
                      icon={<EllipsisVerticalIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Actions"
                      variant="ghost"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, cat.id, 'category')}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Add Category inline form */}
        {addingCategory ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Category name"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ name: e.target.value })}
              autoFocus
              sx={{ ...inputSx, flex: 1 }}
              inputProps={{ style: { fontSize: '0.875rem', padding: '6px 12px' } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddCategory();
                if (e.key === 'Escape') { setAddingCategory(false); setNewCategory({ name: '' }); }
              }}
            />
            <PrimaryButton
              size="small"
              onClick={handleAddCategory}
              disabled={!newCategory.name.trim()}
              loading={savingCategory}
              startIcon={!savingCategory ? <CheckIcon style={{ width: 16, height: 16 }} /> : undefined}
              sx={{ fontSize: '0.813rem', minHeight: 36 }}
            >
              Add
            </PrimaryButton>
            <AppIconButton
              icon={<XMarkIcon style={{ width: 18, height: 18 }} />}
              tooltip="Cancel"
              variant="secondary"
              size="small"
              onClick={() => { setAddingCategory(false); setNewCategory({ name: '' }); }}
            />
          </Box>
        ) : (
          <SecondaryButton
            size="small"
            startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
            onClick={() => setAddingCategory(true)}
            sx={{ fontSize: '0.813rem' }}
          >
            Add Category
          </SecondaryButton>
        )}
      </Paper>

      {/* Payment Modes */}
      <Paper sx={cardSx} elevation={0}>
        {sectionTitle(
          'Payment Modes',
          'Customise the payment methods that appear across expense, income, and settlement forms',
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {paymentModes.length === 0 && (
            <Typography
              variant="body2"
              sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', fontStyle: 'italic' }}
            >
              No payment modes yet — defaults will appear once configured.
            </Typography>
          )}
          {paymentModes.map((mode) => {
            const isEditing = editingPaymentModeId === mode.id;
            return (
              <Box
                key={mode.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '10px',
                  backgroundColor: mode.isActive
                    ? `${theme.primary}08`
                    : theme.gray?.[100] || '#F0F0F0',
                  border: `1px solid ${
                    mode.isActive ? `${theme.primary}33` : theme.gray?.[200] || '#E0E0E0'
                  }`,
                  opacity: mode.isActive ? 1 : 0.6,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={editingPaymentModeDisplayName}
                      onChange={(e) => setEditingPaymentModeDisplayName(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSavePaymentModeDisplayName(mode.id);
                        if (e.key === 'Escape') {
                          setEditingPaymentModeId(null);
                          setEditingPaymentModeDisplayName('');
                        }
                      }}
                      sx={{ ...inputSx, flex: 1 }}
                      inputProps={{ style: { fontSize: '0.875rem', padding: '6px 10px' } }}
                    />
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: theme.text,
                          fontSize: '0.875rem',
                        }}
                      >
                        {mode.displayName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.gray?.[500] || '#666',
                          fontSize: '0.7rem',
                          fontFamily: 'monospace',
                        }}
                      >
                        {mode.name}
                      </Typography>
                      {mode.isDefault && (
                        <Chip
                          label="Default"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            backgroundColor: theme.gray?.[200] || '#E0E0E0',
                            color: theme.gray?.[600] || '#555',
                          }}
                        />
                      )}
                    </>
                  )}
                </Box>

                {isEditing ? (
                  <>
                    <AppIconButton
                      icon={<CheckIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Save"
                      variant="ghost"
                      size="small"
                      onClick={() => handleSavePaymentModeDisplayName(mode.id)}
                      sx={{ color: theme.success, '&:hover': { backgroundColor: `${theme.success}18`, color: theme.success } }}
                    />
                    <AppIconButton
                      icon={<XMarkIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Cancel"
                      variant="ghost"
                      size="small"
                      onClick={() => {
                        setEditingPaymentModeId(null);
                        setEditingPaymentModeDisplayName('');
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Switch
                      checked={mode.isActive}
                      onChange={() => handleTogglePaymentMode(mode)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: theme.primary },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: theme.primary,
                        },
                        '& .MuiSwitch-track': { backgroundColor: theme.gray?.[300] || '#CCC' },
                      }}
                    />
                    <AppIconButton
                      icon={<PencilSquareIcon style={{ width: 16, height: 16 }} />}
                      tooltip="Edit display name"
                      variant="ghost"
                      size="small"
                      onClick={() => {
                        setEditingPaymentModeId(mode.id);
                        setEditingPaymentModeDisplayName(mode.displayName);
                      }}
                    />
                    {!mode.isDefault && (
                      <AppIconButton
                        icon={<TrashIcon style={{ width: 16, height: 16 }} />}
                        tooltip="Delete"
                        variant="ghost"
                        size="small"
                        onClick={() => handleDeletePaymentMode(mode)}
                        sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)', color: 'var(--status-bad)' } }}
                      />
                    )}
                  </>
                )}
              </Box>
            );
          })}
        </Box>

        {addingPaymentMode ? (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 1.5,
              borderRadius: '10px',
              border: `1px solid ${theme.primary}33`,
              backgroundColor: `${theme.primary}06`,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <TextField
              size="small"
              placeholder="e.g. Wallet, Cheque"
              value={newPaymentModeName}
              onChange={(e) => setNewPaymentModeName(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddPaymentMode();
                if (e.key === 'Escape') {
                  setAddingPaymentMode(false);
                  setNewPaymentModeName('');
                }
              }}
              sx={{ ...inputSx, flex: 1, minWidth: 160 }}
              inputProps={{ style: { fontSize: '0.875rem', padding: '8px 12px' } }}
            />
            <PrimaryButton
              onClick={handleAddPaymentMode}
              loading={savingPaymentMode}
              sx={{ minHeight: 36, px: 2, fontSize: '0.813rem' }}
            >
              Add
            </PrimaryButton>
            <SecondaryButton
              onClick={() => {
                setAddingPaymentMode(false);
                setNewPaymentModeName('');
              }}
              sx={{ fontSize: '0.813rem' }}
            >
              Cancel
            </SecondaryButton>
          </Box>
        ) : (
          <SecondaryButton
            onClick={() => setAddingPaymentMode(true)}
            startIcon={<PlusIcon style={{ width: 16, height: 16 }} />}
            sx={{ fontSize: '0.875rem', alignSelf: 'flex-start' }}
          >
            Add Payment Mode
          </SecondaryButton>
        )}
      </Paper>

      {/* Transaction Rules */}
      <Paper sx={cardSx} elevation={0}>
        <TransactionRulesManager embedded />
      </Paper>

      {/* Shared Action Menu for channels + categories */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              minWidth: 140,
            },
          },
        }}
      >
        {menuSection === 'category' && (
          <MenuItem
            onClick={() => {
              const cat = categories.find((c) => c.id === menuActiveId);
              if (cat) { setEditingCategoryId(cat.id); setEditingCategoryName(cat.name); }
              handleMenuClose();
            }}
            sx={{ fontSize: '0.875rem', py: 1 }}
          >
            <ListItemIcon><PencilSquareIcon style={{ width: 16, height: 16, color: theme.primary }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Edit</ListItemText>
          </MenuItem>
        )}
        {menuSection === 'channel' && (
          <MenuItem
            onClick={() => {
              const ch = channels.find((c) => c.id === menuActiveId);
              if (ch) handleOpenEditChannel(ch);
              handleMenuClose();
            }}
            sx={{ fontSize: '0.875rem', py: 1 }}
          >
            <ListItemIcon><PencilSquareIcon style={{ width: 16, height: 16, color: theme.primary }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Edit</ListItemText>
          </MenuItem>
        )}
        {/* Hide Delete for protected defaults (Cash/Card) — they cannot be removed. */}
        {!(menuSection === 'channel'
          && menuActiveId != null
          && (() => {
            const ch = channels.find((c) => c.id === menuActiveId);
            return ch ? isProtectedDefault(ch) : false;
          })()) && (
          <MenuItem
            onClick={() => {
              if (menuSection === 'channel' && menuActiveId != null) {
                handleDeleteChannel(menuActiveId);
              } else if (menuSection === 'category' && menuActiveId != null) {
                handleDeleteCategory(menuActiveId);
              }
              handleMenuClose();
            }}
            sx={{ fontSize: '0.875rem', py: 1, color: theme.danger || '#D99982' }}
          >
            <ListItemIcon><TrashIcon style={{ width: 16, height: 16, color: theme.danger || '#D99982' }} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', color: theme.danger || '#D99982' }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Aggregator onboarding modal — opened via ?modal=aggregator from the
          AggregatorsPage banner. Lists the vendor's existing channels and lets
          them flip one's type to ONLINE_AGGREGATOR in a single click. */}
      <Dialog
        open={aggregatorModalOpen}
        onClose={closeAggregatorModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            backgroundColor: theme.surface || theme.background,
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: theme.text }}>
            Mark a Channel as Aggregator
          </Typography>
          <Typography sx={{ fontSize: '0.813rem', color: theme.gray[600], mt: 0.5 }}>
            Aggregators (Uber Eats, Deliveroo, Just Eat) have different settlement
            and commission rules. Marking a channel here makes the Aggregators page
            filter to delivery revenue only.
          </Typography>
          <IconButton
            onClick={closeAggregatorModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close aggregator modal"
          >
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {channels.length === 0 ? (
            <Typography sx={{ fontSize: '0.875rem', color: theme.gray[600], py: 2 }}>
              No revenue channels yet. Close this dialog and use &ldquo;Add Channel&rdquo;
              below to create one — pick &ldquo;Online Aggregator&rdquo; for the type.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {channels.map((ch) => {
                const isAggregator =
                  ch.type === 'ONLINE_AGGREGATOR' || ch.channelType === 'ONLINE_AGGREGATOR';
                return (
                  <Box
                    key={ch.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: '10px',
                      border: `1px solid ${theme.gray[200]}`,
                      backgroundColor: theme.surface,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: ch.color || theme.gray[400],
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text }}>
                        {ch.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: theme.gray[600] }}>
                        {ch.type || ch.channelType || 'CUSTOM'}
                      </Typography>
                    </Box>
                    {isAggregator ? (
                      <Chip
                        size="small"
                        label="Aggregator"
                        sx={{
                          backgroundColor: `${theme.primary}20`,
                          color: theme.primary,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    ) : (
                      <SecondaryButton
                        size="small"
                        disabled={togglingChannelId === ch.id}
                        onClick={() => handleMarkAsAggregator(ch)}
                        sx={{ fontSize: '0.75rem', flexShrink: 0 }}
                      >
                        {togglingChannelId === ch.id ? 'Saving…' : 'Mark as Aggregator'}
                      </SecondaryButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <SecondaryButton onClick={closeAggregatorModal}>
            Done
          </SecondaryButton>
        </DialogActions>
      </Dialog>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
        <PrimaryButton
          onClick={handleSave}
          loading={saving}
          fullWidth={true}
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            borderRadius: '12px',
            minHeight: 48,
            maxWidth: { sm: 240 },
            boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
            '&:hover': {
              boxShadow: '0 4px 12px color-mix(in srgb, var(--action) 40%, transparent)',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

export default FinanceSettings;
